/**
 * The document register — worker paperwork and company documents.
 *
 * Renders the full list of required document types per worker, present or not. A
 * missing medical certificate is the thing you most need to see, and it is invisible in
 * a list of what has been uploaded.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Badge, Card, ErrorNotice, PageHeader, Spinner, Stat } from '@/components/ui';
import { RegisterPanel } from '@/features/documents/RegisterPanel';
import { SendDocumentsDialog } from '@/features/documents/SendDocumentsDialog';
import { RenameAllDialog } from '@/features/documents/RenameAllDialog';
import { FolderUploadDialog } from '@/features/documents/FolderUploadDialog';
import { ExportSheetButton, ImportSheetDialog } from '@/features/sheets/ExcelSheet';
import { usePermissions } from '@/features/access/usePermissions';
import { useI18n } from '@/i18n';
import { Modal } from '@/components/Modal';
import { MobileList } from '@/components/MobileList';
import { DataToolbar, SortableTh } from '@/components/table';
import { api, type WorkerComplianceRow } from '@/lib/api';
import { useTableState, type SortOption } from '@/lib/table';

const COMPLIANCE_SORTS: SortOption<WorkerComplianceRow>[] = [
  // Worst first by default: this screen exists to surface who is not cleared to work.
  {
    key: 'problems',
    labelKey: 'compliance.toHandleShort',
    value: (r) => r.missingCount * 10 + r.expiredCount * 10 + r.expiringCount,
    defaultDirection: 'desc',
  },
  { key: 'name', labelKey: 'employees.name', value: (r) => r.name },
  { key: 'completeness', labelKey: 'compliance.completeness', value: (r) => r.presentCount / Math.max(1, r.requiredCount) },
];

export function CompliancePage() {
  const { t } = useI18n();
  const [selected, setSelected] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [renamingAll, setRenamingAll] = useState(false);
  const [uploadingFolders, setUploadingFolders] = useState(false);
  const [importingCompany, setImportingCompany] = useState(false);
  const { can } = usePermissions();
  const overview = useQuery({ queryKey: ['compliance'], queryFn: api.workerCompliance });
  const company = useQuery({ queryKey: ['company-register'], queryFn: api.companyRegister });
  const worker = useQuery({
    queryKey: ['worker-register', selected],
    queryFn: () => api.workerRegister(selected!),
    enabled: selected !== null,
  });

  const table = useTableState<WorkerComplianceRow>({
    rows: overview.data?.workers ?? [],
    search: (r) => [r.name],
    sorts: COMPLIANCE_SORTS,
    defaultSortKey: 'problems',
    storageKey: 'compliance',
  });
  const rows = table.rows;
  const totals = {
    missing: rows.reduce((s, r) => s + r.missingCount, 0),
    expired: rows.reduce((s, r) => s + r.expiredCount, 0),
    expiring: rows.reduce((s, r) => s + r.expiringCount, 0),
  };

  const companyItems = company.data?.items ?? [];
  const companyProblems = companyItems.filter(
    (i) => i.status === 'expired' || i.status === 'missing' || i.status === 'expiring',
  ).length;

  return (
    <>
      <PageHeader
        title={t('compliance.title')}
        subtitle={t('compliance.subtitle')}
        actions={
          can('compliance', 'write') ? (
            <>
              <button type="button" className="btn-secondary" onClick={() => setUploadingFolders(true)}>
                {t('bulk.button')}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setRenamingAll(true)}>
                {t('renameAll.button')}
              </button>
              <button type="button" className="btn-primary" onClick={() => setSending(true)}>
                {t('send.button')}
              </button>
            </>
          ) : null
        }
      />
      <SendDocumentsDialog open={sending} onClose={() => setSending(false)} />
      <RenameAllDialog open={renamingAll} onClose={() => setRenamingAll(false)} />
      <FolderUploadDialog open={uploadingFolders} onClose={() => setUploadingFolders(false)} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label={t('common.employees')} value={rows.length} />
        <Stat
          label={t('compliance.missingDocuments')}
          value={totals.missing}
          tone={totals.missing > 0 ? 'danger' : 'success'}
        />
        <Stat
          label={t('compliance.expired')}
          value={totals.expired}
          tone={totals.expired > 0 ? 'danger' : 'success'}
        />
        <Stat
          label={t('compliance.expiring')}
          value={totals.expiring}
          tone={totals.expiring > 0 ? 'warning' : 'success'}
        />
      </div>

      {/* Company documents first — one lapsed DURC stops the whole company bidding. */}
      <div className="flex items-center gap-3 mb-3">
        <h2 className="font-bold">{t('compliance.companyDocuments')}</h2>
        {companyProblems > 0 ? (
          <Badge tone="warning">{t('compliance.toHandle', { count: companyProblems })}</Badge>
        ) : null}
        {/* The company's list in Excel: dates and numbers in one go, like the other lists. */}
        <div className="ms-auto flex gap-2">
          <ExportSheetButton kind="company" />
          {can('compliance', 'write') ? (
            <button type="button" className="btn-secondary" onClick={() => setImportingCompany(true)}>
              {t('sheet.import')}
            </button>
          ) : null}
        </div>
      </div>
      <ImportSheetDialog kind="company" open={importingCompany} onClose={() => setImportingCompany(false)} />

      {/* The company's own folders. "Add" names a new document, sets its deadline and
          files it in one go — the office has more to keep than the standard list. */}
      <div className="mb-8">
        <RegisterPanel scope="company" />
      </div>

      <h2 className="font-bold mb-3">{t('common.employees')}</h2>

      <DataToolbar
        state={table}
        searchPlaceholder={t('employees.searchPlaceholder')}
        countOne="employees.countOne"
        countMany="employees.countMany"
      />

      {overview.isLoading ? (
        <Spinner />
      ) : overview.error ? (
        <ErrorNotice error={overview.error} />
      ) : (
        <Card padded={false} className="mb-6">
          <div className="lg:hidden p-2">
            <MobileList
              rows={rows}
              keyOf={(r) => r.id}
              title={(r) => r.name}
              subtitle={(r) =>
                `${r.presentCount}/${r.requiredCount} \u00b7 ${Math.round(
                  (r.presentCount / Math.max(1, r.requiredCount)) * 100,
                )}%`
              }
              badges={(r) => (
                <>
                  {r.missingCount > 0 ? (
                    <Badge tone="danger">
                      {t('compliance.missing')}: {r.missingCount}
                    </Badge>
                  ) : null}
                  {r.expiredCount > 0 ? (
                    <Badge tone="danger">
                      {t('compliance.expired')}: {r.expiredCount}
                    </Badge>
                  ) : null}
                  {r.expiringCount > 0 ? (
                    <Badge tone="warning">
                      {t('compliance.expiring')}: {r.expiringCount}
                    </Badge>
                  ) : null}
                </>
              )}
              fields={[
                {
                  label: t('compliance.completeness'),
                  value: (r) => `${r.presentCount}/${r.requiredCount}`,
                },
              ]}
              action={(r) => (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setSelected(selected === r.id ? null : r.id)}
                >
                  {t('compliance.openFile')}
                </button>
              )}
            />
          </div>

          <div className="table-scroll max-lg:hidden">
            <table className="table-wide">
              <thead>
                <tr>
                  <SortableTh state={table} sortKey="name">
                    {t('common.employee')}
                  </SortableTh>
                  <SortableTh state={table} sortKey="completeness">
                    {t('compliance.completeness')}
                  </SortableTh>
                  <th className="th">{t('compliance.missing')}</th>
                  <th className="th">{t('compliance.expired')}</th>
                  <th className="th">{t('compliance.expiring')}</th>
                  <th className="th" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const pct = Math.round((r.presentCount / Math.max(1, r.requiredCount)) * 100);
                  return (
                    <tr key={r.id} className="hover:bg-surface-alt">
                      <td className="td font-semibold">{r.name}</td>
                      <td className="td">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-28 rounded-full bg-surface-sunken overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${pct}%`,
                                background:
                                  pct === 100 ? 'var(--color-success)' : 'var(--color-warning)',
                              }}
                            />
                          </div>
                          <span className="text-xs text-muted tabular">
                            {r.presentCount}/{r.requiredCount}
                          </span>
                        </div>
                      </td>
                      <td className="td">
                        {r.missingCount > 0 ? (
                          <Badge tone="danger">{r.missingCount}</Badge>
                        ) : (
                          <span className="text-subtle">—</span>
                        )}
                      </td>
                      <td className="td">
                        {r.expiredCount > 0 ? (
                          <Badge tone="danger">{r.expiredCount}</Badge>
                        ) : (
                          <span className="text-subtle">—</span>
                        )}
                      </td>
                      <td className="td">
                        {r.expiringCount > 0 ? (
                          <Badge tone="warning">{r.expiringCount}</Badge>
                        ) : (
                          <span className="text-subtle">—</span>
                        )}
                      </td>
                      <td className="td text-end">
                        <button
                          type="button"
                          className="td-action text-primary hover:underline"
                          onClick={() => setSelected(selected === r.id ? null : r.id)}
                        >
                          {t(selected === r.id ? 'compliance.closeFile' : 'compliance.openFile')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        size="xl"
        title={worker.data ? t('compliance.fileFor', { name: worker.data.userName }) : ''}
        description={
          worker.data
            ? t('compliance.fileSummary', {
                missing: worker.data.summary.missing,
                expired: worker.data.summary.expired,
                expiring: worker.data.summary.expiring,
              })
            : undefined
        }
      >
        {selected ? <RegisterPanel scope="worker" userId={selected} /> : null}
      </Modal>


    </>
  );
}
